import { useEffect, useRef, useState } from 'react';
import { RoleGuard } from '../../routes';
import {
  BelongsToFilterSelects,
  enqueueSnackbar,
  fullScreenButtonClick,
  ImageGallery,
  Typography,
  YardPlaceScene,
  YardPlaceSceneContainerPlace,
} from '../../design-system';
import {
  useEditContainerMutation,
  useIsSmallScreen,
  useRemovePlaceContainerMutation,
  useStatesWithFilterCounter,
  useYardBlocksSceneQuery,
} from '../../hooks';
import { i18n } from '../../i18n';
import {
  ContainerDetails,
  ContainerHoverInfo,
  EmptySlotContainerSelect,
  SlotHoverInfo,
  ThreeDPlacesPage,
} from './3DPlacesComponents';
import { DashboardHeader } from '../dashboard';
import { MESSAGES } from '../../const';
import { useStoreUser } from '../../context';
import { showToRoles } from '../../utils';

export const ThreeDPlaces = () => {
  const [, setStates, filteredStates] = useStatesWithFilterCounter({
    yardId: '',
  });
  const { data: initialBlocksScene, isFetching } = useYardBlocksSceneQuery(
    [filteredStates.yardId],
    {
      yardId: filteredStates.yardId,
    },
  );

  return (
    <ThreeDPlacesInner
      yardId={filteredStates.yardId}
      initialBlocksScene={initialBlocksScene}
      isFetching={isFetching}
      onFilterChange={(data) => setStates('force', { yardId: data.yardId })}
    />
  );
};

type ThreeDPlacesInnerProps = {
  yardId: string;
  initialBlocksScene: string;
  isFetching: boolean;
  onFilterChange: (data: { yardId: string }) => void;
};

const ThreeDPlacesInner = ({
  yardId,
  initialBlocksScene,
  isFetching,
  onFilterChange,
}: ThreeDPlacesInnerProps) => {
  const { user } = useStoreUser();
  const isSmallScreen = useIsSmallScreen();
  const isViewer = showToRoles(['YARD_COMPANY_VIEWER'], user);
  const { mutate: editContainer } = useEditContainerMutation();
  const { mutate: removePlaceContainer } = useRemovePlaceContainerMutation();

  const blocksScene = useRef(initialBlocksScene);
  const [images, setImages] = useState<any[]>([]);
  const [updateKey, setUpdateKey] = useState(Math.random());
  const [defaultSelected, setDefaultSelected] = useState('');
  const [selectedContainerFocusKey, setSelectedContainerFocusKey] = useState('');
  const [selectedContainerId, setSelectedContainerId] = useState<string>();

  const handleContainerPanelChange = (containerId?: string) => {
    setSelectedContainerId(containerId);
    setDefaultSelected('');
    setSelectedContainerFocusKey('');
  };

  const handleSceneContainerSelectFromDropdown = (container: { id: string; number: string }) => {
    setSelectedContainerId(container.id);
    setDefaultSelected(container.number);
    setSelectedContainerFocusKey(`${container.id}-${Date.now()}`);
  };

  const handleClearSceneContainerSelect = () => {
    setSelectedContainerId(undefined);
    setDefaultSelected('');
    setSelectedContainerFocusKey('');
  };

  useEffect(() => {
    if (isFetching) return;

    blocksScene.current = typeof initialBlocksScene === 'string' ? initialBlocksScene : '';
    setUpdateKey(Math.random());
    setDefaultSelected('');
    setSelectedContainerFocusKey('');
    setSelectedContainerId(undefined);
  }, [yardId, isFetching, initialBlocksScene]);

  const handleClickImage = (containerImages: { url: string }[]) => {
    setImages(containerImages.map(({ url }) => url));
    fullScreenButtonClick('containers');
  };

  const handleDeleteContainer = (containerId: string, rollback: () => void) => {
    removePlaceContainer(containerId, {
      onError: () => {
        rollback();
        enqueueSnackbar(MESSAGES.CONTAINERS.ERROR.NOT_EDITED, 'error');
      },
    });
  };

  const handlePasteContainer = (
    containerId: string,
    place: YardPlaceSceneContainerPlace,
    rollback: () => void,
  ) => {
    editContainer(
      {
        id: containerId,
        block: place.block,
        row: place.row,
        slot: place.slot,
        tier: 20,
      },
      {
        onError: () => {
          rollback();
          enqueueSnackbar(MESSAGES.CONTAINERS.ERROR.NOT_EDITED, 'error');
        },
      },
    );
  };

  return (
    <RoleGuard roles={'adminRead'}>
      <ThreeDPlacesPage>
        <DashboardHeader id="three-d-places-header" isSmallScreen={isSmallScreen}>
          <Typography variant={isSmallScreen ? 'textL' : 'textXL'} weight={'bold'}>
            {i18n.t('pages.modal.yardPlaceSceneTitle')}
          </Typography>
          <BelongsToFilterSelects
            handleChange={onFilterChange}
            filtersType="overview"
            withoutLabel
            isYardRequired
            isSubmitClicked
            onceRequest
          />
        </DashboardHeader>
        <YardPlaceScene
          key={updateKey}
          defaultMode={isViewer ? 'view-containers' : 'edit-containers'}
          defaultValue={typeof blocksScene.current === 'string' ? blocksScene.current : '{}'}
          defaultSelected={defaultSelected}
          selectedContainerFocusKey={selectedContainerFocusKey}
          selectedContainerId={selectedContainerId}
          onSelectedContainerChange={handleContainerPanelChange}
          onDeleteContainer={isViewer ? undefined : handleDeleteContainer}
          onPasteContainer={isViewer ? undefined : handlePasteContainer}
          renderOnEmptyClick={(selectedSlotKey, closeEmptySlot, addContainer, sceneContainers) =>
            !isViewer &&
            !!yardId && (
              <EmptySlotContainerSelect
                yardId={yardId}
                hasSelectedSlot={!!selectedSlotKey}
                sceneContainers={sceneContainers}
                onAddContainer={addContainer}
                onSceneContainerSelect={(container) => {
                  closeEmptySlot?.();
                  handleSceneContainerSelectFromDropdown(container);
                }}
                onClearSceneContainerSelect={handleClearSceneContainerSelect}
              />
            )
          }
          renderOnContainerClick={(containerId, closeContainerDetails, deleteContainer) =>
            !!containerId &&
            !containerId.startsWith('mock-container-id') && (
              <ContainerDetails
                containerId={containerId}
                onClose={() => closeContainerDetails?.()}
                handleClickImage={handleClickImage}
                onDelete={isViewer ? undefined : () => deleteContainer?.()}
              />
            )
          }
          renderOnSlotHover={(info) =>
            info && <SlotHoverInfo place={info.place} tier={info.tier} x={info.x} y={info.y} />
          }
          renderOnContainerHover={(info) =>
            info && (
              <ContainerHoverInfo
                number={info.number}
                type={info.type}
                clientCode={info.clientCode}
                place={info.place}
                tier={info.tier}
                x={info.x}
                y={info.y}
              />
            )
          }
        />
      </ThreeDPlacesPage>
      <ImageGallery id={'containers'} photos={images} startIndex={0} />
    </RoleGuard>
  );
};
